import MapBox from './../modules/map'
import { CountryRiskRatingPopupComponent } from '../components/CountryRiskRatingPopup/CountryRiskRatingPopup'
import { DetailsPanel } from '../sections/luxury-yachting/components/DetailsPanel/DetailsPanel'
import { getStandarisedInternationalSOSMedicalRiskRating } from './../services/utils'
import { COUNTRY_RISK_LAYER_STYLES } from './../data/country-risk-layer-styles'
import { COUNTRY_DETAILS } from '../data/country-details'

export class CountryRiskModule {
    countryRiskRatings = {}
    stylesModule = 'default'
    enableTravelRisk = true
    enableMedicalRisk = false

    initialize(countryRiskRatings) {
        this.countryRiskRatings = countryRiskRatings
        this.displayLayers()
        this.initializeEventListeners()

        CountryRiskRatingPopupComponent.$data.enableTravelRisk = this.enableTravelRisk
        CountryRiskRatingPopupComponent.$data.enableMedicalRisk = this.enableMedicalRisk
    }

    displayLayers() {
        MapBox.map.addSource('countryTiles', {
            type: 'vector',
            url: 'mapbox://mapbox.country-boundaries-v1',
            disputed: false,
            worldview: ['US'],
            promoteId: { 'country_boundaries': 'iso_3166_1' }
        })

        this.setCountryRiskStates()

        MapBox.map.addLayer({
            id: 'risk-country-layer',
            type: 'fill',
            source: 'countryTiles',
            'source-layer': 'country_boundaries',
            filter: [
                'any',
                ['==', 'all', ['get', 'worldview']],
                ['in', 'US', ['get', 'worldview']]
            ],
            layout: {
                visibility: 'none'
            },
            paint: COUNTRY_RISK_LAYER_STYLES[this.stylesModule]['risk-country-layer'].paint
        }, 'waterway-label')
    }

    setCountryRiskStates(riskRatingType = 'travelrisk') {
        this.countryRiskRatings.regions.forEach(region => {
            region.destinations.forEach(destination => {
                if (destination.type === 'country') {
                    let riskValue = destination.risks[riskRatingType]
                    if (riskRatingType === 'medicalrisk') {
                        riskValue = getStandarisedInternationalSOSMedicalRiskRating(riskValue)
                    }
                    MapBox.map.setFeatureState(
                        {
                            source: 'countryTiles',
                            sourceLayer: 'country_boundaries',
                            id: destination.isocode.toUpperCase()
                        },
                        {
                            riskRating: riskValue
                        }
                    )
                }
            })
        })
    }

    _showLayers(riskRatingType) {
        MapBox.map.setLayoutProperty('risk-country-layer', 'visibility', 'visible')
        document.getElementsByClassName('app-template')[0].classList.add(`${riskRatingType}-risk-enabled`)
    }

    _hideLayers(riskRatingType) {
        MapBox.map.setLayoutProperty('risk-country-layer', 'visibility', 'none')
        document.getElementsByClassName('app-template')[0].classList.remove(`${riskRatingType}-risk-enabled`)
    }

    showTravelRiskLayers() {
        this.setCountryRiskStates('travelrisk')
        this._showLayers('travel')
    }

    hideTravelRiskLayers() {
        this._hideLayers('travel')
    }

    showMedicalRiskLayers() {
        this.setCountryRiskStates('medicalrisk')
        this._showLayers('medical')
    }

    hideMedicalRiskLayers() {
        this._hideLayers('medical')
    }

    hideRiskRatingPopup() {
        CountryRiskRatingPopupComponent.$data.display = 'none'
        CountryRiskRatingPopupComponent.$data.travelRiskRating = 0
        CountryRiskRatingPopupComponent.$data.medicalRiskRating = 0
        CountryRiskRatingPopupComponent.$data.countryName = ''
    }

    getCountryTravelRiskRating(ISO_3166_1) {
        let countryRiskRating = 0
        let lowerCasedISOCode = ISO_3166_1.toLowerCase()
        for (const region of this.countryRiskRatings.regions) {
            countryRiskRating = region.destinations.find(destination => destination.isocode === lowerCasedISOCode)
            if (countryRiskRating !== undefined) break
        }

        return countryRiskRating !== undefined ? countryRiskRating.risks.travelrisk : 0
    }

    getCountryMedicalRiskRating(ISO_3166_1) {
        let riskRating = 0
        let lowerCasedISOCode = ISO_3166_1.toLowerCase()
        for (const region of this.countryRiskRatings.regions) {
            riskRating = region.destinations.find(destination => destination.isocode === lowerCasedISOCode)
            if (riskRating !== undefined) break
        }

        return riskRating !== undefined ? getStandarisedInternationalSOSMedicalRiskRating(riskRating.risks.medicalrisk) : 0
    }

    showCountry(ISO_3166_1, ISO_3166_1_ALPHA_3, nameEN, medicalOnly = false) {
        this.highlightCountry(ISO_3166_1)

        DetailsPanel.showCountryDetailsPanel({
            name: nameEN,
            countryRating: this.getCountryTravelRiskRating(ISO_3166_1),
            countryMedicalRating: this.getCountryMedicalRiskRating(ISO_3166_1),
            ISO_3166_1_ALPHA_2: ISO_3166_1,
            ISO_3166_1_ALPHA_3: ISO_3166_1_ALPHA_3
        }, medicalOnly)
    }

    highlightCountry(ISO_3166_1_ALPHA_2) {
        const existingLayer = MapBox.map.getLayer('country-layer')
        if (existingLayer === undefined) {
            // Country fill
            MapBox.map.addLayer({
                id: 'country-layer',
                type: 'fill',
                source: 'countryTiles',
                'source-layer': 'country_boundaries',
                filter: ['any', ['all', ['==', ['get', 'iso_3166_1'], ISO_3166_1_ALPHA_2]]],
                paint: COUNTRY_RISK_LAYER_STYLES[this.stylesModule]['country-layer'].paint
            }, 'waterway-label')

            // Country boundary line
            MapBox.map.addLayer({
                id: 'country-layer-line',
                type: 'line',
                source: 'countryTiles',
                'source-layer': 'country_boundaries',
                filter: ['any', ['all', ['==', ['get', 'iso_3166_1'], ISO_3166_1_ALPHA_2]]],
                paint: COUNTRY_RISK_LAYER_STYLES[this.stylesModule]['country-layer-line'].paint
            }, 'waterway-label')
        } else {
            MapBox.map.setFilter('country-layer', ['any', ['all', ['==', ['get', 'iso_3166_1'], ISO_3166_1_ALPHA_2]]])
            MapBox.map.setFilter('country-layer-line', ['any', ['all', ['==', ['get', 'iso_3166_1'], ISO_3166_1_ALPHA_2]]])
            MapBox.map.setLayoutProperty('country-layer', 'visibility', 'visible')
            MapBox.map.setLayoutProperty('country-layer-line', 'visibility', 'visible')
        }

        let countryData = COUNTRY_DETAILS.find(res => res.ISO_3166_1_ALPHA_2 === ISO_3166_1_ALPHA_2)
        if (countryData.centralLat && countryData.centralLng) {
            MapBox.map.flyTo({ center: ['' + countryData.centralLng, '' + countryData.centralLat], zoom: 3.45 })
        }

    }

    unhighlightCountry() {
        const existingLayer = MapBox.map.getLayer('country-layer')
        if (existingLayer !== undefined) {
            MapBox.map.setLayoutProperty('country-layer', 'visibility', 'none')
            MapBox.map.setLayoutProperty('country-layer-line', 'visibility', 'none')
        }
    }

    initializeEventListeners() {
        let hoveredCountryId = null

        MapBox.map.on('mousemove', 'risk-country-layer', (e) => {
            if (e.features.length > 0) {
                if (hoveredCountryId !== null) {
                    MapBox.map.setFeatureState(
                        {
                            source: 'countryTiles',
                            sourceLayer: 'country_boundaries',
                            id: hoveredCountryId
                        },
                        {
                            hover: false
                        }
                    )
                }

                hoveredCountryId = e.features[0].id
                MapBox.map.setFeatureState(
                    {
                        source: 'countryTiles',
                        sourceLayer: 'country_boundaries',
                        id: hoveredCountryId
                    },
                    {
                        hover: true
                    }
                )
            }

            const countryData = e.features[0].properties
            // Basically we want to show the country layer popup, only if a city marker is not hovered already
            // to avoid both elements overlapping, we want it also to be really performant, so we are going to do checks
            // with native browser document ID selector and check for the style property that we manipulate
            if (countryData.iso_3166_1 !== undefined && document.getElementById('city-popup').style.opacity === '0') {
                CountryRiskRatingPopupComponent.$data.display = 'block'
                CountryRiskRatingPopupComponent.$data.countryName = countryData.name_en

                CountryRiskRatingPopupComponent.$data.travelRiskRating = this.getCountryTravelRiskRating(countryData.iso_3166_1)
                CountryRiskRatingPopupComponent.$data.medicalRiskRating = this.getCountryMedicalRiskRating(countryData.iso_3166_1)

                MapBox.map.getCanvas().style.cursor = 'pointer'

                CountryRiskRatingPopupComponent.$data.leftPosition = e.originalEvent.x + 15 + 'px'
                CountryRiskRatingPopupComponent.$data.topPosition = e.originalEvent.y + 20 + 'px'
            } else {
                this.hideRiskRatingPopup()
            }
        })

        // When the mouse leaves the state-fill layer, update the feature state of the previously hovered feature.
        MapBox.map.on('mouseleave', 'risk-country-layer', () => {
            if (hoveredCountryId !== null) {
                MapBox.map.setFeatureState(
                    {
                        source: 'countryTiles',
                        sourceLayer: 'country_boundaries',
                        id: hoveredCountryId
                    },
                    {
                        hover: false
                    }
                )
            }

            hoveredCountryId = null

            MapBox.map.getCanvas().style.cursor = 'inherit'

            this.hideRiskRatingPopup()
        })
    }
}

const CountryRisk = new CountryRiskModule()
export default CountryRisk
